'use client';

import * as amplitude from '@amplitude/analytics-browser';
import { getOrSetViewerId, resetViewerId } from '@modules/analytics/helpers';
import { logger } from '@modules/logger';
import { getCookie } from 'cookies-next';

import { isMeaningfulTraitValue } from '../identify-traits';
import type { AnalyticsPlugin } from '../types';

function getGaContext(): { sessionId: string | null } {
  return { sessionId: getCookie('_ga4') ?? '' };
}

const platform = 'dash';
const version = 1;

export default function amplitudePlugin(): AnalyticsPlugin {
  let initialized = false;

  return {
    name: 'amplitude',

    initialize: () => {
      if (typeof window === 'undefined') return;
      if (initialized) return;

      const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_ANALYTICS_KEY;
      const serverUrl = process.env.NEXT_PUBLIC_AMPLITUDE_SERVER_URL;

      // Fetch existing device_id cookie if available
      const deviceId = getOrSetViewerId();

      if (!apiKey) {
        logger.error('Amplitude API key is missing');
        return;
      }

      amplitude.init(apiKey, {
        deviceId,
        autocapture: false,
        serverUrl: serverUrl ?? undefined,
      });

      initialized = true;
    },

    page: ({ payload }) => {
      // Segment adds the Viewed prefix to page_view events
      // Explicitly adding 'Viewed ' it here for consistency
      const eventName = 'Viewed page_view';

      const eventProperties = {
        ...payload.properties,
        host: payload.properties?.host ?? window.location.hostname,
        search: window.location.search,
        platform,
        version,
        name: payload.properties?.name ?? 'page_view',
        ga: getGaContext(),
        referrer: document.referrer,
        url: window.location.href,
        title: document.title,
      };

      try {
        amplitude.track(eventName, eventProperties);
      } catch (error) {
        logger.warn('Error calling Amplitude page()', { error });
      }
    },

    track: ({ payload }) => {
      console.log('Amplitude Plugin Track Event', payload);
      const eventProperties = {
        ...payload.properties,
        platform,
        version,
        ga: getGaContext(),
      };

      try {
        amplitude.track(payload.event, eventProperties);
      } catch (error) {
        logger.warn('Error calling Amplitude track()', { error });
      }
    },

    identify: ({ payload: { userId, traits } }) => {
      try {
        const identify = new amplitude.Identify();
        if (traits) {
          for (const [key, value] of Object.entries(traits)) {
            if (isMeaningfulTraitValue(value)) {
              identify.set(key, value);
            } else if (value !== null && value !== undefined) {
              // Present but blank/invalid: never write it — it would clobber a profile property that
              // Experiment targeting reads (e.g. email:'' breaks every email-targeted flag). Surface the
              // caller so the source can be fixed.
              logger.warn('Amplitude identify: skipped empty user property', { key });
            }
          }
        }
        if (userId && !userId?.includes('anon_')) {
          // never set anon or viewer IDs as user ID
          amplitude.setUserId(userId);
        } else {
          // device ID is already set during init,
          // clear user ID to prevent user ID override
          amplitude.setUserId(undefined);
        }
        amplitude.identify(identify);
      } catch (error) {
        logger.error('Error calling Amplitude identify()', { error });
      }
    },

    loaded: () => {
      return initialized;
    },

    reset: () => {
      const newDeviceId = resetViewerId();
      amplitude.setUserId(undefined);
      amplitude.setDeviceId(newDeviceId);
    },
  };
}
